import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Save, Calendar, MapPin, Users, Shield, Languages, Mail, Plus, Trash2 } from 'lucide-react';

const MEETING_PURPOSES = [
  'Initial Evaluation', 'Reevaluation', 'Annual IEP Review', 'IEP Amendment',
  'Eligibility Determination', 'Manifestation Determination', 'Transition Planning', 'Other',
];

const NOTICE_METHODS = ['Mail', 'Email', 'Hand Delivered', 'Phone', 'Other'];
const SAFEGUARD_METHODS = ['Included with Notice', 'Email', 'Hand Delivered', 'Mail', 'Previously Provided'];
const PARTICIPANT_ROLES = [
  'Parent / Guardian', 'General Education Teacher', 'Special Education Teacher',
  'School Psychologist', 'Speech-Language Pathologist', 'Administrator / LEA Representative',
  'Occupational Therapist', 'Physical Therapist', 'School Counselor',
  'Student', 'Interpreter', 'Other',
];

function mergeContent(existing) {
  const defaults = {
    meeting_date: '', meeting_time: '', meeting_purpose: '', meeting_location: '',
    participants_invited: [],
    parental_rights_provided: { provided: false, date: '', method: '' },
    notice_sent_date: '', notice_method: '',
    interpreter_needed: { needed: false, language: '' },
    notes: '',
  };
  if (!existing || typeof existing !== 'object') return defaults;
  return {
    ...defaults, ...existing,
    participants_invited: existing.participants_invited || [],
    parental_rights_provided: { ...defaults.parental_rights_provided, ...(existing.parental_rights_provided || {}) },
    interpreter_needed: { ...defaults.interpreter_needed, ...(existing.interpreter_needed || {}) },
  };
}

export default function MeetingNoticeEditor({ document, student, onSave, saving }) {
  const [content, setContent] = useState(() => mergeContent(document?.content));
  useEffect(() => { setContent(mergeContent(document?.content)); }, [document?.document_id]);

  const update = (key, value) => setContent(prev => ({ ...prev, [key]: value }));
  const updateNested = (parent, key, value) => setContent(prev => ({
    ...prev, [parent]: { ...prev[parent], [key]: value },
  }));

  const addParticipant = () => {
    setContent(prev => ({
      ...prev, participants_invited: [...prev.participants_invited, { name: '', role: '', invited: true }],
    }));
  };
  const removeParticipant = (idx) => {
    setContent(prev => ({
      ...prev, participants_invited: prev.participants_invited.filter((_, i) => i !== idx),
    }));
  };
  const updateParticipant = (idx, key, value) => {
    setContent(prev => ({
      ...prev, participants_invited: prev.participants_invited.map((p, i) => i === idx ? { ...p, [key]: value } : p),
    }));
  };

  const handleSave = () => onSave(content);

  return (
    <div className="max-w-4xl space-y-6" data-testid="meeting-notice-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]" data-testid="meeting-notice-title">Meeting Notice</h2>
          <p className="text-sm text-[#595959] mt-1">Notify parents/guardians and team members of an upcoming meeting.</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl shrink-0" data-testid="meeting-notice-save-top">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {/* Student Info Banner */}
      {student && (
        <div className="bg-[#F3F4F1] border border-[#E5E5E5] rounded-2xl p-5" data-testid="notice-student-info">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-3">Student Information</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div><span className="text-xs text-[#595959]">Name</span><p className="font-medium text-[#1C1C1C]">{student.first_name} {student.last_name}</p></div>
            <div><span className="text-xs text-[#595959]">Grade</span><p className="font-medium text-[#1C1C1C]">{student.grade || '—'}</p></div>
            <div><span className="text-xs text-[#595959]">DOB</span><p className="font-medium text-[#1C1C1C]">{student.date_of_birth || '—'}</p></div>
            <div><span className="text-xs text-[#595959]">Student ID</span><p className="font-medium text-[#1C1C1C]">{student.student_id?.slice(-8) || '—'}</p></div>
          </div>
        </div>
      )}

      {/* Meeting Details */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="meeting-details-section">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-4">
          <Calendar className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />Meeting Details
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Meeting Date</label>
            <Input type="date" value={content.meeting_date} onChange={e => update('meeting_date', e.target.value)} className="rounded-xl border-[#E5E5E5] text-sm h-9" data-testid="meeting-date" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Meeting Time</label>
            <Input type="time" value={content.meeting_time} onChange={e => update('meeting_time', e.target.value)} className="rounded-xl border-[#E5E5E5] text-sm h-9" data-testid="meeting-time" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Purpose</label>
            <Select value={content.meeting_purpose || undefined} onValueChange={v => update('meeting_purpose', v)}>
              <SelectTrigger className="rounded-xl text-sm h-9" data-testid="meeting-purpose"><SelectValue placeholder="Select purpose" /></SelectTrigger>
              <SelectContent>{MEETING_PURPOSES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">
            <MapPin className="h-3 w-3 inline mr-1 -mt-0.5" />Location
          </label>
          <Input value={content.meeting_location} onChange={e => update('meeting_location', e.target.value)} placeholder="Room number, building, or virtual meeting link" className="rounded-xl border-[#E5E5E5] text-sm h-9" data-testid="meeting-location" />
        </div>
      </div>

      {/* Participants Invited */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="meeting-participants-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959]">
            <Users className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />Participants Invited
          </h3>
          <Button variant="outline" size="sm" onClick={addParticipant} className="rounded-xl text-xs h-7 border-[#E5E5E5]" data-testid="add-participant-btn">
            <Plus className="h-3 w-3 mr-1" />Add Participant
          </Button>
        </div>
        {content.participants_invited.length === 0 ? (
          <p className="text-sm text-[#595959] italic py-4 text-center">No participants added yet. Click "Add Participant" to begin.</p>
        ) : (
          <div className="space-y-3">
            {content.participants_invited.map((p, idx) => (
              <div key={idx} className="flex items-start gap-3 border border-[#E5E5E5] rounded-xl p-3" data-testid={`participant-row-${idx}`}>
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-[#595959] mb-1 block">Name</label>
                    <Input value={p.name} onChange={e => updateParticipant(idx, 'name', e.target.value)} placeholder="Full name" className="rounded-xl border-[#E5E5E5] text-sm h-8" />
                  </div>
                  <div>
                    <label className="text-xs text-[#595959] mb-1 block">Role</label>
                    <Select value={p.role || undefined} onValueChange={v => updateParticipant(idx, 'role', v)}>
                      <SelectTrigger className="rounded-xl text-sm h-8"><SelectValue placeholder="Select role" /></SelectTrigger>
                      <SelectContent>{PARTICIPANT_ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end gap-2">
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-[#595959] pb-1">
                      <input type="checkbox" checked={p.invited !== false} onChange={e => updateParticipant(idx, 'invited', e.target.checked)} className="rounded text-[#3B6A9B] focus:ring-[#3B6A9B]" />
                      Invited
                    </label>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeParticipant(idx)} className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl h-8 w-8 p-0 mt-5" data-testid={`remove-participant-${idx}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notice Delivery */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="notice-delivery-section">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-4">
          <Mail className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />Notice Delivery
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Notice Sent Date</label>
            <Input type="date" value={content.notice_sent_date} onChange={e => update('notice_sent_date', e.target.value)} className="rounded-xl border-[#E5E5E5] text-sm h-9" data-testid="notice-sent-date" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Delivery Method</label>
            <Select value={content.notice_method || undefined} onValueChange={v => update('notice_method', v)}>
              <SelectTrigger className="rounded-xl text-sm h-9" data-testid="notice-method"><SelectValue placeholder="Select method" /></SelectTrigger>
              <SelectContent>{NOTICE_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Procedural Safeguards */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="procedural-safeguards-section">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-4">
          <Shield className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />Procedural Safeguards
        </h3>
        <div className="mb-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={content.parental_rights_provided.provided} onChange={e => updateNested('parental_rights_provided', 'provided', e.target.checked)} className="rounded text-[#3B6A9B] focus:ring-[#3B6A9B] h-4 w-4" data-testid="safeguards-provided-checkbox" />
            <span className="text-sm text-[#1C1C1C]">Procedural safeguards notice has been provided to parent/guardian</span>
          </label>
        </div>
        {content.parental_rights_provided.provided && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3 pl-7">
            <div>
              <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Date Provided</label>
              <Input type="date" value={content.parental_rights_provided.date} onChange={e => updateNested('parental_rights_provided', 'date', e.target.value)} className="rounded-xl border-[#E5E5E5] text-sm h-9" data-testid="safeguards-date" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Method</label>
              <Select value={content.parental_rights_provided.method || undefined} onValueChange={v => updateNested('parental_rights_provided', 'method', v)}>
                <SelectTrigger className="rounded-xl text-sm h-9" data-testid="safeguards-method"><SelectValue placeholder="Select method" /></SelectTrigger>
                <SelectContent>{SAFEGUARD_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      {/* Interpreter */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="interpreter-section">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-4">
          <Languages className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />Interpreter Services
        </h3>
        <div className="mb-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={content.interpreter_needed.needed} onChange={e => updateNested('interpreter_needed', 'needed', e.target.checked)} className="rounded text-[#3B6A9B] focus:ring-[#3B6A9B] h-4 w-4" data-testid="interpreter-needed-checkbox" />
            <span className="text-sm text-[#1C1C1C]">An interpreter is needed for this meeting</span>
          </label>
        </div>
        {content.interpreter_needed.needed && (
          <div className="mt-3 pl-7 max-w-xs">
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Language</label>
            <Input value={content.interpreter_needed.language} onChange={e => updateNested('interpreter_needed', 'language', e.target.value)} placeholder="e.g., Spanish" className="rounded-xl border-[#E5E5E5] text-sm h-9" data-testid="interpreter-language" />
          </div>
        )}
      </div>

      {/* Additional Notes */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="notice-notes-section">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-4">Additional Notes</h3>
        <Textarea value={content.notes} onChange={e => update('notes', e.target.value)} placeholder="Any additional information regarding this meeting notice..." rows={3} className="rounded-xl border-[#E5E5E5] text-sm" data-testid="notice-notes" />
      </div>

      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl" data-testid="meeting-notice-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Meeting Notice'}
        </Button>
      </div>
    </div>
  );
}
